import { useEffect, useState } from 'react';

/**
 * One click to copy. This is the single most-used action in a snippet manager, so it stays
 * prominent and never hides behind a menu.
 */
export function CopyButton({ text, label = 'Copy' }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) {
      return;
    }

    const timer = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(timer);
  }, [copied]);

  return (
    <button
      type="button"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
        } catch {
          // Clipboard access can be refused (insecure origin, denied permission). Saying nothing
          // would look like the click did nothing at all.
          setCopied(false);
          window.prompt('Copy this manually:', text);
        }
      }}
      className="inline-flex items-center gap-1.5 rounded-md border border-border-subtle
        bg-surface px-2.5 py-1 text-xs font-medium text-text transition-colors
        hover:border-border-strong"
    >
      <span aria-hidden="true">{copied ? '✓' : '⧉'}</span>
      {copied ? 'Copied' : label}
      {/* Announced without moving focus or disturbing the visible label. */}
      <span className="sr-only" role="status" aria-live="polite">
        {copied ? 'Copied to clipboard' : ''}
      </span>
    </button>
  );
}
